import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { Clock, X } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "src/primitives/Popover";
import {
  Field,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
} from "src/primitives/Field";
import { cn } from "src/shadcn/lib/utils";
import { useControlledOpen } from "src/hooks/useControlledOpen";
import { useCursorRestore } from "src/hooks/useCursorRestore";
import { useOutsideClickClose } from "src/hooks/useOutsideClickClose";

import type { Dayjs } from "dayjs";

import { TimePickerPanel } from "./TimePickerPanel";
import { SIZE_CONFIG } from "./constants";
import type { TimePickerProps, TimeValue } from "./types";
import {
  coerceToTimeValue,
  formatTimeDisplay,
  formatTimeWithPattern,
  getDefaultTimeMaskFormat,
  getTimePlaceholder,
  parseTimeString,
  parseTimeWithPattern,
  timeValueEquals,
  timeValueToDayjs,
} from "./utils";
import { applyMask, buildMaskTemplate } from "src/utils/inputMask";

type RangeSide = "from" | "to";

const TimePicker = forwardRef<HTMLDivElement, TimePickerProps>(
  (
    {
      value,
      onChange,
      onBlur,
      type = "time",
      format = "24",
      displayFormat,
      showSeconds = false,
      minuteStep = 1,
      secondStep = 1,
      disabled = false,
      placeholder,
      label,
      error,
      helpText,
      size = "medium",
      required = false,
      timezone,
      open: openProp,
      onOpenChange,
      needConfirm = true,
      onOk,
      labelProps,
      className,
    },
    ref
  ) => {
    const generatedId = useId();
    const errorId = `error_${generatedId}`;
    const helpTextId = `helpText_${generatedId}`;
    const containerRef = useRef<HTMLDivElement>(null);
    const fromInputRef = useRef<HTMLInputElement>(null);
    const toInputRef = useRef<HTMLInputElement>(null);
    const fromCursorRef = useRef<number | null>(null);
    const toCursorRef = useRef<number | null>(null);

    const popoverContentId = useRef(
      `timepicker-popover-${generatedId}`
    ).current;

    const committedRef = useRef<{
      from: TimeValue | null;
      to: TimeValue | null;
    }>({ from: null, to: null });

    const { open, setOpen } = useControlledOpen(openProp, onOpenChange);

    const isRange = type === "range";

    const getInitialTimes = useCallback(() => {
      if (isRange) {
        if (!value || !Array.isArray(value)) {
          return { from: null, to: null };
        }

        return {
          from: coerceToTimeValue(value[0] as TimeValue | Date | Dayjs | null),
          to: coerceToTimeValue(value[1] as TimeValue | Date | Dayjs | null),
        };
      }

      return {
        from: coerceToTimeValue(
          value as TimeValue | Date | Dayjs | null | undefined
        ),
        to: null,
      };
    }, [value, isRange]);

    const formatOne = useCallback(
      (time: TimeValue | null) =>
        displayFormat
          ? formatTimeWithPattern(time, displayFormat)
          : formatTimeDisplay(time, format, showSeconds),
      [displayFormat, format, showSeconds]
    );

    const [activeSide, setActiveSide] = useState<RangeSide>("from");
    const [fromTime, setFromTime] = useState(() => getInitialTimes().from);
    const [toTime, setToTime] = useState(() => getInitialTimes().to);

    const [fromText, setFromText] = useState(() =>
      formatOne(getInitialTimes().from)
    );
    const [toText, setToText] = useState(() => formatOne(getInitialTimes().to));

    const parseOne = (text: string): TimeValue | null =>
      displayFormat
        ? parseTimeWithPattern(text, displayFormat)
        : parseTimeString(text, format);

    const emitChange = (from: TimeValue | null, to: TimeValue | null) => {
      if (isRange) {
        if (!from || !to) {
          onChange?.(null, ["", ""]);

          return;
        }

        onChange?.(
          [timeValueToDayjs(from), timeValueToDayjs(to)],
          [formatOne(from), formatOne(to)]
        );

        return;
      }

      if (!from) {
        onChange?.(null, "");

        return;
      }

      onChange?.(timeValueToDayjs(from), formatOne(from));
    };

    const commitPendingOnClose = useCallback(() => {
      if (!needConfirm) {
        setFromText(formatOne(fromTime));
        setToText(formatOne(toTime));

        return;
      }

      const { from: committedFrom, to: committedTo } = committedRef.current;
      const unchanged =
        timeValueEquals(fromTime, committedFrom) &&
        timeValueEquals(toTime, committedTo);
      if (unchanged) return;

      emitChange(fromTime, toTime);
      committedRef.current = { from: fromTime, to: toTime };
    }, [needConfirm, fromTime, toTime, formatOne, isRange, onChange]);

    const closePopover = useCallback(() => {
      commitPendingOnClose();
      setOpen(false);
    }, [commitPendingOnClose, setOpen]);

    useOutsideClickClose({
      enabled: open,
      containerRef,
      popoverElementId: popoverContentId,
      onClose: closePopover,
    });

    const maskTemplate = useMemo(
      () =>
        displayFormat
          ? null
          : buildMaskTemplate(getDefaultTimeMaskFormat(format, showSeconds)),
      [displayFormat, format, showSeconds]
    );

    const handleOk = () => {
      if (isRange && activeSide === "from" && fromTime && !toTime) {
        setActiveSide("to");
        toInputRef.current?.focus();

        return;
      }

      committedRef.current = { from: fromTime, to: toTime };
      emitChange(fromTime, toTime);

      if (isRange) {
        if (fromTime && toTime) {
          onOk?.([timeValueToDayjs(fromTime), timeValueToDayjs(toTime)]);
        } else {
          onOk?.(null);
        }
      } else {
        onOk?.(fromTime ? timeValueToDayjs(fromTime) : null);
      }

      setOpen(false);
    };

    const handlePanelChange = (time: TimeValue) => {
      if (!isRange) {
        setFromTime(time);
        setFromText(formatOne(time));
        if (!needConfirm) emitChange(time, null);

        return;
      }

      if (activeSide === "from") {
        setFromTime(time);
        setFromText(formatOne(time));
        if (!needConfirm) emitChange(time, toTime);

        return;
      }

      setToTime(time);
      setToText(formatOne(time));
      if (!needConfirm) emitChange(fromTime, time);
    };

    const buildInputChangeHandler =
      (side: RangeSide) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let text = e.target.value;
        const previousText = side === "from" ? fromText : toText;

        if (maskTemplate) {
          const masked = applyMask(
            text,
            maskTemplate,
            previousText,
            e.target.selectionStart
          );
          text = masked.text;

          if (side === "from") fromCursorRef.current = masked.cursorPosition;
          else toCursorRef.current = masked.cursorPosition;
        }

        if (side === "from") setFromText(text);
        else setToText(text);

        const parsed = parseOne(text);
        if (!parsed) return;

        if (side === "from") {
          setFromTime(parsed);
          if (!needConfirm) emitChange(parsed, toTime);
        } else {
          setToTime(parsed);
          if (!needConfirm) emitChange(fromTime, parsed);
        }
      };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        const { from, to } = committedRef.current;
        setFromTime(from);
        setToTime(to);
        setFromText(formatOne(from));
        setToText(formatOne(to));
        setOpen(false);

        return;
      }

      if (e.key !== "Enter") return;

      if (needConfirm) {
        handleOk();

        return;
      }

      setFromText(formatOne(fromTime));
      setToText(formatOne(toTime));
      setOpen(false);
    };

    const handleInputFocus = (side: RangeSide) => {
      setActiveSide(side);
      if (!open) setOpen(true);
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const next = e.relatedTarget as HTMLElement | null;
      if (next && containerRef.current?.contains(next)) return;

      const popoverEl = document.getElementById(popoverContentId);
      if (next && popoverEl?.contains(next)) return;

      onBlur?.(e);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      setFromTime(null);
      setToTime(null);
      setFromText("");
      setToText("");

      committedRef.current = { from: null, to: null };
      onChange?.(null, isRange ? ["", ""] : "");
    };

    useEffect(() => {
      if (value === undefined) return;

      const next = getInitialTimes();
      setFromTime(next.from);
      setToTime(next.to);
      if (open) return;

      setFromText(formatOne(next.from));
      setToText(formatOne(next.to));
      committedRef.current = { from: next.from, to: next.to };
    }, [value, getInitialTimes, formatOne, open]);

    useEffect(() => {
      if (!open) return;

      committedRef.current = { from: fromTime, to: toTime };
    }, [open]);

    useCursorRestore([
      { inputRef: fromInputRef, cursorRef: fromCursorRef },
      { inputRef: toInputRef, cursorRef: toCursorRef },
    ]);

    const singlePlaceholder = getTimePlaceholder(
      format,
      showSeconds,
      displayFormat
    );

    const hasField = !!(label || error || helpText);
    const sizeConfig = SIZE_CONFIG[size];
    const hasAnyValue = !!(fromText || toText);
    const activeTime = activeSide === "from" ? fromTime : toTime;

    const ariaDescribedBy =
      [error ? errorId : null, helpText ? helpTextId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    const inputBaseClass = cn(
      "min-w-0 bg-transparent outline-none placeholder:text-muted-foreground",
      "disabled:cursor-not-allowed",
      sizeConfig.input
    );

    const triggerContent = (
      <Popover open={open}>
        <PopoverAnchor asChild>
          <div
            ref={containerRef}
            className={cn(
              "relative flex w-full items-center gap-2 rounded-md border border-input bg-transparent pe-8 ps-3",
              "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
              disabled && "cursor-not-allowed opacity-50",
              !!error && "border-destructive ring-destructive/20 ring-3",
              sizeConfig.trigger,
              !hasField && className
            )}
          >
            {timezone ? (
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                {timezone.toUpperCase()}
              </span>
            ) : (
              <Clock
                className={cn(
                  "shrink-0 text-muted-foreground",
                  sizeConfig.icon
                )}
              />
            )}
            {isRange ? (
              <>
                <input
                  ref={fromInputRef}
                  type="text"
                  disabled={disabled}
                  aria-label={
                    label ? `${label} start time` : "Pick a start time"
                  }
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={!!error || undefined}
                  placeholder={placeholder ?? singlePlaceholder}
                  value={fromText}
                  onChange={buildInputChangeHandler("from")}
                  onKeyDown={handleInputKeyDown}
                  onFocus={() => handleInputFocus("from")}
                  onBlur={handleInputBlur}
                  className={cn(inputBaseClass, "flex-1")}
                />
                <span
                  aria-hidden="true"
                  className="shrink-0 text-muted-foreground"
                >
                  ~
                </span>
                <input
                  ref={toInputRef}
                  type="text"
                  disabled={disabled}
                  aria-label={label ? `${label} end time` : "Pick an end time"}
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={!!error || undefined}
                  placeholder={placeholder ?? singlePlaceholder}
                  value={toText}
                  onChange={buildInputChangeHandler("to")}
                  onKeyDown={handleInputKeyDown}
                  onFocus={() => handleInputFocus("to")}
                  onBlur={handleInputBlur}
                  className={cn(inputBaseClass, "flex-1")}
                />
              </>
            ) : (
              <input
                ref={fromInputRef}
                type="text"
                disabled={disabled}
                aria-label={label || "Pick a time"}
                aria-describedby={ariaDescribedBy}
                aria-invalid={!!error || undefined}
                placeholder={placeholder ?? singlePlaceholder}
                value={fromText}
                onChange={buildInputChangeHandler("from")}
                onKeyDown={handleInputKeyDown}
                onFocus={() => handleInputFocus("from")}
                onBlur={handleInputBlur}
                className={cn(inputBaseClass, "flex-1")}
              />
            )}
            <button
              type="button"
              onPointerDown={e => e.preventDefault()}
              onClick={handleClear}
              className={cn(
                "absolute inset-y-0 end-2 flex items-center text-muted-foreground hover:text-foreground focus:outline-none",
                !(hasAnyValue && !disabled) && "invisible"
              )}
              aria-label={isRange ? "Clear time range" : "Clear time"}
              tabIndex={-1}
            >
              <X className={sizeConfig.icon} />
            </button>
          </div>
        </PopoverAnchor>
        {open && (
          <PopoverContent
            id={popoverContentId}
            className="w-auto p-0"
            align="start"
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
            onPointerDownOutside={e => e.preventDefault()}
            onInteractOutside={e => e.preventDefault()}
          >
            <TimePickerPanel
              value={activeTime ?? undefined}
              onChange={handlePanelChange}
              format={format}
              showSeconds={showSeconds}
              minuteStep={minuteStep}
              secondStep={secondStep}
              disabled={disabled}
            />
            {needConfirm && (
              <div className="flex items-center justify-end border-t border-border px-3 py-2">
                <button
                  type="button"
                  onClick={handleOk}
                  className="rounded bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  OK
                </button>
              </div>
            )}
          </PopoverContent>
        )}
      </Popover>
    );

    if (!hasField) {
      return (
        <div ref={ref} className={className}>
          {triggerContent}
        </div>
      );
    }

    return (
      <Field
        ref={ref}
        data-disabled={disabled || undefined}
        data-invalid={!!error || undefined}
        className={className}
      >
        {label && (
          <FieldLabel
            {...(labelProps as React.ComponentProps<typeof FieldLabel>)}
          >
            {label}
            {required && (
              <span aria-hidden="true" className="text-destructive">
                *
              </span>
            )}
          </FieldLabel>
        )}
        <FieldContent>
          {triggerContent}
          {!!error && <FieldError id={errorId}>{error}</FieldError>}
          {helpText && (
            <FieldDescription id={helpTextId}>{helpText}</FieldDescription>
          )}
        </FieldContent>
      </Field>
    );
  }
);

TimePicker.displayName = "TimePicker";

export { TimePicker };
